import React from "react";
import { styled, SxProps, Box, Stack, Typography, Card, CardActionArea, Chip, Button, Checkbox } from "@mui/material";

import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ConfigContentRecaps, {
    ConfigContentRecapsProps,
} from "../../Config/ConfigContentRecaps/config-content-row-recap";
import OrderStatusIcon from "../../Misc/Status/OrderStatus/order-status";
import PosterImage from "../../Misc/PosterImage";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import OrderProductIcon from "../../Misc/OrderProductIcon/order-product-icon";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { useTranslation } from "react-i18next";

export type OrderCardProps = {
    className?: string;
    sx?: SxProps;
    title: string;
    subtitle: string;
    thumbnail: string;
    config: {
        title: string;
        type: OrderProductType;
        status: OrderStatus;
        price: string;
        content: ConfigContentRecapsProps;
        attachement?: string;
        activities: number;
    };
    onClick?: React.MouseEventHandler;
    onSelectClick?: (item: DbOrderItemInfos) => void;
    index: number;
    item: DbOrderItemInfos;
    selected?: boolean;
    selectedOnClick?: boolean;
};

const OrderCard: React.FC<OrderCardProps> = (props) => {
    const { t } = useTranslation();
    const { config, title, subtitle, thumbnail, onClick, onSelectClick, selectedOnClick, index, item, selected, sx } = props;

    const handleCheckboxClick: React.MouseEventHandler = (e) => {
        e.stopPropagation();

        if (!onSelectClick) {
            return;
        }

        onSelectClick(item);
    };

    const handleCardClick: React.MouseEventHandler = (e: React.MouseEvent) => {
        if (selectedOnClick && onSelectClick) {
            onSelectClick(item);
        } else if (onClick) {
            onClick(e);
        }
    };

    return (
        <Wrapper variant="outlined" aria-selected={selected}>
            <CardActionArea component="div" role="button" onClick={handleCardClick}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5} sx={{ width: "100%" }}>
                    <OrderProductIcon type={config.type} color="info" sx={{ width: 32, height: 32 }} />
                    <Stack direction="row" alignItems="center">
                        <CardActionRow className="action-row" direction="row" alignItems="center">
                            <Button size="small" onClick={(e) => e.stopPropagation()} variant="icon" sx={{ mr: 0.5 }}>
                                <ChatOutlinedIcon />
                            </Button>
                            <Button size="small" onClick={(e) => e.stopPropagation()} variant="icon" sx={{ mr: 2 }}>
                                <InfoOutlinedIcon color="error" />
                            </Button>
                        </CardActionRow>
                        <Checkbox checked={selected || false} onClick={handleCheckboxClick} />
                    </Stack>
                </Stack>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    {config.title}
                </Typography>
                <Box sx={{ color: "text.secondary" }} mb={1}>
                    <ConfigContentRecaps {...config.content} />
                </Box>
                <Stack direction="row" spacing={0.5}>
                    <OrderStatusIcon status={config.status} />
                    <Chip
                        label={config.activities}
                        color={config.activities > 0 ? "info" : "default"}
                        icon={<ChatOutlinedIcon />}
                    />
                </Stack>
                <Box sx={{ marginTop: "auto", width: "100%" }}>
                    <Stack direction="row" alignItems="center" spacing={2} mt={3}>
                        <PosterImage img={{ src: thumbnail, loading: "lazy" }} width={54} height={72} minWidth={54} />
                        <Stack>
                            <Typography variant="subtitle2" fontWeight={500} mb={1}>
                                {title}
                            </Typography>
                            <Typography variant="subtitle2" color="text.tier" fontWeight={400}>
                                {subtitle}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" mt={1.5}>
                        <Typography color="text.secondary">{t("common:price")}</Typography>
                        <Typography fontWeight={500}>{config.price}</Typography>
                    </Stack>
                </Box>
            </CardActionArea>
        </Wrapper>
    );
};

export default OrderCard;

const CardActionRow = styled(Stack)(({ theme }) => ({
    opacity: 0,
    transition: theme.transitions.create("opacity", {
        duration: theme.transitions.duration.short,
        easing: theme.transitions.easing.easeOut,
    }),
}));

const Wrapper = styled(Card)(({ theme }) => ({
    borderRadius: 6,
    backgroundImage: "none",
    boxShadow: "none",
    transition: "background .3s",
    background: theme.palette.background.paper,
    "&:hover": {
        borderColor: theme.palette.backgroundAlpha.borderFocus,
        background: theme.palette.backgroundAlpha.main,
        ".action-row": {
            opacity: 1,
        },
    },
    ".MuiCardActionArea-root": {
        padding: theme.spacing(2),
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
    },
    ".MuiCardActionArea-focusHighlight": {
        opacity: 1,
        backgroundColor: "transparent",
    },
}));
